import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plug, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const backfillEta = (startedAt, progress) => {
  if (!startedAt || !progress || progress <= 0 || progress >= 100) return null;
  const elapsed = (Date.now() - new Date(startedAt).getTime()) / 1000;
  if (elapsed < 5) return null;
  const remaining = Math.max((elapsed / (progress / 100)) - elapsed, 0);
  if (remaining < 60) return '≈ menos de 1 min restante';
  const mins = Math.round(remaining / 60);
  if (mins < 60) return `≈ ${mins} min restantes`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `≈ ${h}h${m > 0 ? ' ' + m + 'min' : ''} restantes`;
};

export const Integrations = () => {
  const { selectedProject } = useApp();
  const navigate = useNavigate();
  
  // Global GA4 state
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  
  // GA4 Properties list
  const [ga4Properties, setGa4Properties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  
  // Project GA4 settings
  const [projectSettings, setProjectSettings] = useState(null);
  const [projectConfig, setProjectConfig] = useState({
    ga4_property_id: '',
    measurement_id: '',
    default_conversion_event: 'purchase',
    is_active: false
  });
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [loadingProjectSettings, setLoadingProjectSettings] = useState(true);

  // Wake Commerce per-project settings
  const [wakeSettings, setWakeSettings] = useState(null);
  const [wakeToken, setWakeToken] = useState('');
  const [wakeActive, setWakeActive] = useState(false);
  const [replacingToken, setReplacingToken] = useState(false);
  const [loadingWake, setLoadingWake] = useState(false);
  const [savingWake, setSavingWake] = useState(false);
  const [testingWake, setTestingWake] = useState(false);
  const [syncingWake, setSyncingWake] = useState(false);
  const [reimportingWake, setReimportingWake] = useState(false);

  // Load global GA4 settings on mount
  useEffect(() => {
    loadGlobalSettings();
  }, []);

  // Load project settings when project changes
  useEffect(() => {
    if (selectedProject && globalSettings?.is_active) {
      loadProjectSettings();
      loadGa4Properties();
    }
  }, [selectedProject, globalSettings]);

  // Load Wake settings when project changes (independent of GA4 global config)
  useEffect(() => {
    if (selectedProject) {
      loadWakeSettings();
    }
  }, [selectedProject]);

  const loadGlobalSettings = async () => {
    try {
      setLoadingGlobal(true);
      const response = await axios.get(`${API_URL}/api/integrations/ga4/global-settings`);
      setGlobalSettings(response.data);
    } catch (error) {
      console.error('Error loading global GA4 settings:', error);
      setGlobalSettings(null);
    } finally {
      setLoadingGlobal(false);
    }
  };

  const loadProjectSettings = async () => {
    if (!selectedProject) return;
    
    try {
      setLoadingProjectSettings(true);
      const response = await axios.get(
        `${API_URL}/api/integrations/projects/${selectedProject.id}/ga4-settings`
      );
      setProjectSettings(response.data);
      setProjectConfig({
        ga4_property_id: response.data.ga4_property_id || '',
        measurement_id: response.data.measurement_id || '',
        default_conversion_event: response.data.default_conversion_event || 'purchase',
        is_active: response.data.is_active || false
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error loading project GA4 settings:', error);
      }
      setProjectSettings(null);
      setProjectConfig({
        ga4_property_id: '',
        measurement_id: '',
        default_conversion_event: 'purchase',
        is_active: false
      });
    } finally {
      setLoadingProjectSettings(false);
    }
  };

  const loadGa4Properties = async () => {
    try {
      setLoadingProperties(true);
      const response = await axios.get(`${API_URL}/api/integrations/ga4/properties`);
      if (response.data.success && response.data.properties) {
        setGa4Properties(response.data.properties);
      } else {
        setGa4Properties([]);
      }
    } catch (error) {
      console.error('Error loading GA4 properties:', error);
      setGa4Properties([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  const saveProjectConfig = async () => {
    if (!selectedProject) return;
    
    if (!projectConfig.ga4_property_id) {
      toast.error('Selecione uma propriedade do GA4');
      return;
    }

    setIsConfiguring(true);
    try {
      await axios.post(
        `${API_URL}/api/integrations/projects/${selectedProject.id}/ga4-settings`,
        {
          project_id: selectedProject.id,
          ga4_property_id: projectConfig.ga4_property_id,
          measurement_id: projectConfig.measurement_id || null,
          default_conversion_event: projectConfig.default_conversion_event || 'purchase',
          is_active: projectConfig.is_active
        }
      );
      toast.success('Configuração do GA4 salva com sucesso!');
      await loadProjectSettings();
    } catch (error) {
      console.error('Error saving project config:', error);
      toast.error('Erro ao salvar configuração do GA4');
    } finally {
      setIsConfiguring(false);
    }
  };

  const loadWakeSettings = async () => {
    if (!selectedProject) return;
    try {
      setLoadingWake(true);
      const response = await axios.get(
        `${API_URL}/api/integrations/projects/${selectedProject.id}/wake-settings`
      );
      setWakeSettings(response.data);
      setWakeActive(response.data.is_active || false);
      setWakeToken('');
      setReplacingToken(!response.data.has_token);
    } catch (error) {
      console.error('Error loading Wake settings:', error);
      setWakeSettings(null);
      setWakeActive(false);
      setWakeToken('');
      setReplacingToken(true);
    } finally {
      setLoadingWake(false);
    }
  };

  const saveWakeConfig = async () => {
    if (!selectedProject) return;
    if (wakeActive && !wakeSettings?.has_token && !wakeToken.trim()) {
      toast.error('Informe o token da Wake para ativar a integração.');
      return;
    }
    setSavingWake(true);
    try {
      const payload = { is_active: wakeActive };
      if (wakeToken.trim()) payload.api_token = wakeToken.trim();
      await axios.post(
        `${API_URL}/api/integrations/projects/${selectedProject.id}/wake-settings`,
        payload
      );
      toast.success('Configuração da Wake salva com sucesso!');
      await loadWakeSettings();
    } catch (error) {
      console.error('Error saving Wake config:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração da Wake');
    } finally {
      setSavingWake(false);
    }
  };

  const testWakeConnection = async () => {
    if (!selectedProject) return;
    if (wakeToken.trim()) {
      toast.info('Salve o token antes de testar a conexão.');
      return;
    }
    if (!wakeSettings?.has_token) {
      toast.error('Salve um token da Wake antes de testar a conexão.');
      return;
    }
    setTestingWake(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/integrations/projects/${selectedProject.id}/wake-settings/test`
      );
      if (response.data.status === 'ok') {
        toast.success('Conexão com a Wake estabelecida!');
      } else {
        toast.error(response.data.message || 'Falha na conexão com a Wake.');
      }
      await loadWakeSettings();
    } catch (error) {
      console.error('Error testing Wake connection:', error);
      toast.error(error.response?.data?.detail || 'Erro ao testar conexão com a Wake');
    } finally {
      setTestingWake(false);
    }
  };

  const syncWakeNow = async () => {
    if (!selectedProject) return;
    setSyncingWake(true);
    try {
      await axios.post(
        `${API_URL}/api/integrations/projects/${selectedProject.id}/wake-settings/sync`
      );
      toast.success('Sincronização iniciada. Os dados serão atualizados em segundo plano.');
      setTimeout(loadWakeSettings, 3000);
    } catch (error) {
      console.error('Error syncing Wake:', error);
      toast.error(error.response?.data?.detail || 'Erro ao iniciar a sincronização da Wake');
    } finally {
      setSyncingWake(false);
    }
  };

  const reimportWakeHistory = async () => {
    if (!selectedProject) return;
    setReimportingWake(true);
    try {
      await axios.post(
        `${API_URL}/api/integrations/projects/${selectedProject.id}/wake-settings/reimport`
      );
      toast.success('Reimportação do histórico iniciada. O progresso aparece abaixo.');
      setTimeout(loadWakeSettings, 2000);
    } catch (error) {
      console.error('Error reimporting Wake history:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reimportar o histórico da Wake');
    } finally {
      setReimportingWake(false);
    }
  };

  // Poll while a history backfill is in progress so the progress bar updates live
  useEffect(() => {
    if (!wakeSettings?.is_active || wakeSettings?.backfill_completed) return undefined;
    const id = setInterval(loadWakeSettings, 10000);
    return () => clearInterval(id);
  }, [wakeSettings?.is_active, wakeSettings?.backfill_completed]);

  if (!selectedProject) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Selecione um projeto para configurar integrações.</p>
      </div>
    );
  }

  if (loadingGlobal) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Check if global GA4 is configured
  const globalGA4Configured = globalSettings?.is_active;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Integrações</h1>
        <p className="text-sm text-muted-foreground">
          Configure integrações deste projeto, como Google Analytics 4.
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="ga4" className="w-full">
        <TabsList>
          <TabsTrigger value="ga4" className="gap-2">
            <Plug className="h-4 w-4" />
            GA4
          </TabsTrigger>
          <TabsTrigger value="wake" className="gap-2" data-testid="wake-tab-trigger">
            <Plug className="h-4 w-4" />
            Wake Commerce
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ga4" className="space-y-6 mt-6">
          {/* Global GA4 not configured warning */}
          {!globalGA4Configured && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-orange-900 mb-2">
                      Integração global do GA4 não configurada
                    </h3>
                    <p className="text-sm text-orange-800 mb-4">
                      Para configurar o GA4 deste projeto, primeiro é necessário configurar as credenciais 
                      globais do GA4 nas configurações do sistema.
                    </p>
                    <Button 
                      onClick={() => navigate('/system-settings')}
                      variant="outline"
                      className="border-orange-300 bg-white hover:bg-orange-100"
                    >
                      Ir para Configurações do sistema
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* GA4 Configuration Form */}
          {globalGA4Configured && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Configuração do GA4 para este projeto</CardTitle>
                    <CardDescription>
                      Selecione a propriedade do GA4 que representa este projeto e configure as opções de relatório
                    </CardDescription>
                  </div>
                  {projectSettings?.is_active && (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Ativa
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingProjectSettings ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    {/* Property Selector */}
                    <div className="space-y-2">
                      <Label htmlFor="propertySelect">Propriedade do GA4 *</Label>
                      {loadingProperties ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Carregando propriedades...
                        </div>
                      ) : (
                        <select
                          id="propertySelect"
                          value={projectConfig.ga4_property_id}
                          onChange={(e) => setProjectConfig({ ...projectConfig, ga4_property_id: e.target.value })}
                          className="w-full border rounded px-3 py-2 bg-background"
                          disabled={isConfiguring}
                        >
                          <option value="">Selecione uma propriedade</option>
                          {ga4Properties.map((prop) => {
                            const displayText = prop.account_name 
                              ? `${prop.account_name} – ${prop.display_name}`
                              : prop.display_name;
                            return (
                              <option 
                                key={prop.property_id} 
                                value={prop.property_id}
                                title={displayText}
                              >
                                {displayText}
                              </option>
                            );
                          })}
                        </select>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Propriedades disponíveis na conta GA4 configurada globalmente
                      </p>
                    </div>

                    {/* Measurement ID */}
                    <div className="space-y-2">
                      <Label htmlFor="measurementId">Measurement ID (opcional)</Label>
                      <Input
                        id="measurementId"
                        placeholder="G-XXXXXXXXXX"
                        value={projectConfig.measurement_id}
                        onChange={(e) => setProjectConfig({ ...projectConfig, measurement_id: e.target.value })}
                        disabled={isConfiguring}
                      />
                      <p className="text-xs text-muted-foreground">
                        Exemplo: G-ABC123XYZ (encontrado em Admin → Fluxos de dados)
                      </p>
                    </div>

                    {/* Default Conversion Event */}
                    <div className="space-y-2">
                      <Label htmlFor="conversionEvent">Evento de conversão padrão (opcional)</Label>
                      <Input
                        id="conversionEvent"
                        placeholder="purchase"
                        value={projectConfig.default_conversion_event}
                        onChange={(e) => setProjectConfig({ ...projectConfig, default_conversion_event: e.target.value })}
                        disabled={isConfiguring}
                      />
                      <p className="text-xs text-muted-foreground">
                        Exemplo: purchase, generate_lead, sign_up
                      </p>
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                      <div className="space-y-0.5">
                        <Label className="text-base">Ativar relatórios para este projeto</Label>
                        <p className="text-sm text-muted-foreground">
                          Habilita a visualização de métricas do GA4 na página de Relatórios
                        </p>
                      </div>
                      <Switch
                        checked={projectConfig.is_active}
                        onCheckedChange={(checked) => setProjectConfig({ ...projectConfig, is_active: checked })}
                        disabled={isConfiguring}
                      />
                    </div>

                    {/* Save Button */}
                    <Button onClick={saveProjectConfig} disabled={isConfiguring} className="w-full">
                      {isConfiguring ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        'Salvar integração deste projeto'
                      )}
                    </Button>

                    {/* Success State */}
                    {projectSettings?.is_active && (
                      <div className="flex items-start gap-2 p-4 rounded-lg bg-green-50 border border-green-200">
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-900">Integração ativa</p>
                          <p className="text-xs text-green-700 mt-1">
                            O dashboard de relatórios está disponível na página de Relatórios.
                          </p>
                          <Button 
                            variant="link" 
                            className="p-0 h-auto text-green-700 hover:text-green-900 mt-2"
                            onClick={() => navigate('/reports')}
                          >
                            Ver Relatórios →
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="wake" className="space-y-6 mt-6" data-testid="wake-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>Wake Commerce</CardTitle>
              <CardDescription>
                Configure o token de integração da Wake para este projeto e teste a conexão.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingWake ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Token field */}
                  <div className="space-y-2">
                    <Label htmlFor="wakeToken">Token de integração (REST)</Label>
                    {wakeSettings?.has_token && !replacingToken ? (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium" data-testid="wake-token-saved">
                          Token salvo: ••••{wakeSettings.token_last4}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="wake-replace-token-btn"
                          onClick={() => { setReplacingToken(true); setWakeToken(''); }}
                        >
                          Substituir token
                        </Button>
                      </div>
                    ) : (
                      <Input
                        id="wakeToken"
                        type="password"
                        data-testid="wake-token-input"
                        placeholder="Cole o token da Wake"
                        value={wakeToken}
                        onChange={(e) => setWakeToken(e.target.value)}
                        disabled={savingWake}
                      />
                    )}
                    <p className="text-xs text-muted-foreground" data-testid="wake-help-text">
                      Gere o token no painel da Wake em Configurações Administrativas → Token Integração
                      → Adicionar Token → Nova Integração - REST, com permissão de leitura de Pedidos.
                    </p>
                  </div>

                  {/* Active switch */}
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                    <div className="space-y-0.5">
                      <Label className="text-base">Ativar integração</Label>
                      <p className="text-sm text-muted-foreground">
                        Habilita a integração com a Wake Commerce para este projeto.
                      </p>
                    </div>
                    <Switch
                      data-testid="wake-active-switch"
                      checked={wakeActive}
                      onCheckedChange={setWakeActive}
                      disabled={savingWake}
                    />
                  </div>

                  {/* Status badge */}
                  {wakeSettings?.last_test_status && (
                    <div data-testid="wake-status-badge">
                      {wakeSettings.last_test_status === 'ok' ? (
                        <div className="flex items-start gap-2 p-4 rounded-lg bg-green-50 border border-green-200">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-600 hover:bg-green-600">Conectado</Badge>
                              {wakeSettings.last_test_at && (
                                <span className="text-xs text-green-700">
                                  {new Date(wakeSettings.last_test_at).toLocaleString('pt-BR')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-green-800 mt-1">{wakeSettings.last_test_message}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 border border-red-200">
                          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive">Erro</Badge>
                              {wakeSettings.last_test_at && (
                                <span className="text-xs text-red-700">
                                  {new Date(wakeSettings.last_test_at).toLocaleString('pt-BR')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-red-800 mt-1">{wakeSettings.last_test_message}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {wakeSettings?.has_token && wakeSettings?.products_permission != null && (
                    <div className={`p-3 rounded-lg border text-sm ${wakeSettings.products_permission ? 'bg-background' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid="wake-products-permission">
                      {wakeSettings.products_permission ? (
                        <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" />Produtos e estoque: com permissão
                          {wakeSettings.products_last_sync_at && (
                            <span className="text-xs text-muted-foreground">· {wakeSettings.products_variants_count ?? 0} variantes, atualizado em {new Date(wakeSettings.products_last_sync_at).toLocaleString('pt-BR')}</span>
                          )}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2"><AlertCircle className="h-4 w-4 text-amber-600" />Produtos e estoque: sem permissão. Adicione leitura de Produtos e Estoque ao token da Wake.</span>
                      )}
                    </div>
                  )}

                  {/* History backfill progress */}
                  {wakeSettings?.is_active && !wakeSettings?.backfill_completed && wakeSettings?.sync_status !== 'erro' && (
                    <div className="p-3 rounded-lg border bg-background space-y-2" data-testid="wake-backfill-progress">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Importando histórico (13 meses)…</span>
                        <span className="font-mono font-medium" data-testid="wake-backfill-percent">{wakeSettings?.backfill_progress ?? 0}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-amber-500 transition-all duration-500"
                          style={{ width: `${wakeSettings?.backfill_progress ?? 0}%` }}
                        />
                      </div>
                      {backfillEta(wakeSettings?.backfill_started_at, wakeSettings?.backfill_progress) && (
                        <p className="text-xs text-muted-foreground" data-testid="wake-backfill-eta">
                          {backfillEta(wakeSettings?.backfill_started_at, wakeSettings?.backfill_progress)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Sync status + manual sync */}
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-background" data-testid="wake-sync-status">
                    <div className="text-sm">
                      {wakeSettings?.is_active && !wakeSettings?.backfill_completed && wakeSettings?.sync_status !== 'erro' ? (
                        <span className="text-muted-foreground">Importando histórico: {wakeSettings?.backfill_progress ?? 0}%</span>
                      ) : wakeSettings?.last_sync_at ? (
                        <span className="text-muted-foreground">
                          Última sincronização: {new Date(wakeSettings.last_sync_at).toLocaleString('pt-BR')}
                          {' — '}
                          <span className={wakeSettings.sync_status === 'erro' ? 'text-red-600 font-medium' : 'text-green-700 font-medium'}>
                            {wakeSettings.sync_status === 'erro' ? 'erro' : wakeSettings.sync_status || 'ok'}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Nenhuma sincronização realizada ainda.</span>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="wake-sync-now-btn"
                      onClick={syncWakeNow}
                      disabled={syncingWake || !wakeSettings?.is_active || !wakeSettings?.has_token}
                    >
                      {syncingWake ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sincronizando...</>
                      ) : (
                        'Sincronizar agora'
                      )}
                    </Button>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      data-testid="wake-test-btn"
                      onClick={testWakeConnection}
                      disabled={testingWake || savingWake}
                    >
                      {testingWake ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Testando...</>
                      ) : (
                        'Testar conexão'
                      )}
                    </Button>
                    <Button
                      data-testid="wake-save-btn"
                      onClick={saveWakeConfig}
                      disabled={savingWake || testingWake}
                    >
                      {savingWake ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvando...</>
                      ) : (
                        'Salvar'
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="wake-reimport-btn"
                      onClick={reimportWakeHistory}
                      disabled={reimportingWake || !wakeSettings?.is_active || !wakeSettings?.has_token}
                    >
                      {reimportingWake ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reiniciando...</>
                      ) : (
                        'Reimportar histórico'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
